import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import StaticField from '../../../Common/Controls/StaticField';
import { RootState } from '../../../../../../redux/root';
import { getPermitDocumentComments, getProjectDocumentFiles } from '../../../../../../api/projectDocuments';
import { CommentItem } from '../../../../types';
import CommentsTable from '../../../Common/CommentsTable';
import UploadFiles from '../../../Common/UploadFiles';
import { saveClientDocuments } from '../../../../../../api/document';
import { dispatch } from '../../../../../../redux/store';
import { setFilesToUpload, setPermitDocuments, setSelectedDocument } from '../../../../../../redux/slices/documentsSlice';
import NotApplicableFiles from '../NotApplicableFiles';
import ActionFiles from './ActionFiles';
import FileEntry from '../../../../../../pages/Permits/DocumentsTable/FileEntry';

type Props = {};

const AvailableActions: FC<Props> = ({}) => {
  const selectedDocument = useSelector((s: RootState) => s.documents.SelectedDocument);
  const notApplicables = useSelector((s: RootState) => s.documents.notApplicables);
  const permitDocuments = useSelector((s: RootState) => s.documents.PermitDocuments);
  const filesToUpload = useSelector((s: RootState) => s.documents.filesToUpload);
  const { name, statusTitle, adminFiles, pk } = selectedDocument;
  const [comments, setComments] = useState<CommentItem[]>([]);
  const { handleSubmit, register, errors, setValue, getValues, watch, reset } = useForm({});
  const [forceDisable, setForceDisable] = useState<boolean>(false);
  const filesSelected: boolean = watch('upload', false);
  const [filesToUploadMissing, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    loadFiles();
  }, [pk]);

  useEffect(() => {
    if (forceDisable) {
      onSubmit(null);
    }
  }, [forceDisable]);

  const loadFiles = () => {
    if (pk) {
      const loadComment = async () => {
        const params = {
          offset: 0,
          is_active: true,
          is_deleted: false,
          limit: 100000000,
          system_message: false,
          doc: pk,
        };
        const result = await getPermitDocumentComments(`${pk}`, params);
        const sortedComments = result.data.results.sort((a, b) => {
          if (new Date(a.createdAt) > new Date(b.createdAt)) return -1;
          if (new Date(a.createdAt) < new Date(b.createdAt)) return 1;
          return 0;
        });
        setComments(sortedComments);
      };
      loadComment();
    } else {
      setComments([]);
    }
  };

  const onSubmit = async form => {
    const formData = new FormData();
    const files = getValues('documentFile');
    if (files.length === 0) {
      setShowError(true);
      setErrorMsg('You must select files to upload!');
      return;
    }
    setShowError(false);

    if (!name) {
      setShowError(true);
      setErrorMsg('You must select a document!');
      return;
    }
    formData.append('permit_document_id', `${pk}`);
    files.forEach((file: any) => {
      formData.append('attachments_files', file);
    });

    try {
      await saveClientDocuments(formData);
      reset();
      loadFiles();
      dispatch(setFilesToUpload([]));

      const result = await getProjectDocumentFiles({}, `${pk}`);
      const _selectedDoc = {
        ...selectedDocument,
        clientFiles: result.data.clientFiles,
      };
      dispatch(setSelectedDocument(_selectedDoc));

      const _permitDocuments = permitDocuments.map(doc => {
        if (doc.pk === result.data.pk) {
          return _selectedDoc;
        }
        return doc;
      });
      dispatch(setPermitDocuments(_permitDocuments));
      setForceDisable(false);
    } catch (e) {
      console.log(e);
      setForceDisable(false);
    }
  };

  const allowFileUpload = statusTitle === 'Waiting on client' && !notApplicables[pk];

  if (!pk) {
    return <EmptyState>Select a document on the left to see actions.</EmptyState>;
  }

  return (
    <Root>
      {adminFiles && adminFiles.length > 0 && (
        <Group>
          <GroupLabel>Document</GroupLabel>
          <AdminFiles>
            {adminFiles.map((f, index) => (
              <FileEntry key={index} file={f} row={selectedDocument} fileType="admin" refetch={() => {}} />
            ))}
            <AdminHint>Click above to open the document.</AdminHint>
          </AdminFiles>
        </Group>
      )}

      <StatusRow>
        <StaticField label="Status:" value={statusTitle} />
      </StatusRow>

      <Group>
        <GroupLabel>Comments</GroupLabel>
        {comments.length === 0 ? <EmptyLine>No comments yet.</EmptyLine> : <CommentsTable comments={comments} />}
      </Group>

      {statusTitle === 'Waiting on client' && (
        <Group>
          <NotApplicableFiles />
        </Group>
      )}

      <ActionFiles />

      {allowFileUpload && (
        <form onSubmit={handleSubmit(onSubmit)}>
          {filesToUploadMissing && <Error>{errorMsg}</Error>}
          <UploadFiles showList setValue={setValue} register={register} />
          <UploadButtonRow>
            <UploadBtn type="button" disabled={filesToUpload.length === 0 || forceDisable} onClick={() => setForceDisable(true)}>
              Upload
            </UploadBtn>
          </UploadButtonRow>
        </form>
      )}
    </Root>
  );
};

export default AvailableActions;

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif;
`;

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const GroupLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const StatusRow = styled.div`
  padding: 10px 12px;
  background: var(--neutral-50);
  border: 1px solid var(--border);
  border-radius: 8px;
`;

const AdminFiles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AdminHint = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-500);
  font-style: italic;
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

const EmptyLine = styled.div`
  padding: 8px 2px;
  color: var(--neutral-500);
  font-size: 0.8125rem;
`;

const UploadButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
`;

const UploadBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 140px;
  height: 36px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: var(--primary-600);
  color: var(--neutral-0);
  font-family: inherit;
  font-weight: 600;
  font-size: 0.8125rem;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    background: var(--neutral-200);
    color: var(--neutral-500);
    cursor: not-allowed;
  }
`;

const Error = styled.div`
  color: var(--danger-500, #e0423f);
  font-size: 0.8125rem;
  padding: 8px 12px;
  background: var(--danger-50, #fdecec);
  border: 1px solid var(--danger-100, #f9c9c9);
  border-radius: 8px;
  margin-bottom: 10px;
`;
